import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { api } from '@/lib/axios';

export interface RankingPlayerRow {
  CharID: number;
  CharName16: string;
  CurLevel: number;
  GuildName: string;
  ItemPoints: number;
  RefObjID: number;
  MasteryID: number;
  MaxMasteryLevel: number;
  MasteryName: string;
  Rank?: number;
  Score?: string;
}

export interface RankingResponse {
  success: boolean;
  data: RankingPlayerRow[];
  metadata?: {
    total: number;
    last_update: string | null;
    load_time_ms: number;
    type: string;
    limit: number;
  };
}

const STALE_MS = 120_000;
const RANKING_PAGE_SIZE = 8;

export async function fetchRanking(
  type = 'itempoints',
  limit = 500,
  force = false,
): Promise<RankingResponse> {
  const { data } = await api.get<RankingResponse>('/api/v1/ranking', {
    params: { type, limit, ...(force ? { force: '1' } : {}) },
  });
  return data;
}

export function useRanking(type = 'itempoints', limit = 500) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['ranking', type, limit],
    queryFn: async () => {
      let result = await fetchRanking(type, limit);
      if (type === 'itempoints' && result.data.length === RANKING_PAGE_SIZE) {
        const forced = await fetchRanking(type, limit, true);
        if (forced.data.length > result.data.length) {
          result = forced;
        }
      }
      return result;
    },
    staleTime: STALE_MS,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return failureCount < 1;
      }
      return failureCount < 2;
    },
  });

  const refreshRanking = async () => {
    const fresh = await fetchRanking(type, limit, true);
    const current = queryClient.getQueryData<RankingResponse>(['ranking', type, limit]);
    const nextRows = fresh.data?.length ? fresh.data : (current?.data ?? []);
    const merged: RankingResponse = {
      ...fresh,
      data: nextRows,
      metadata: {
        ...fresh.metadata,
        total: Math.max(fresh.metadata?.total ?? 0, nextRows.length),
        type: fresh.metadata?.type ?? type,
        limit: fresh.metadata?.limit ?? limit,
        last_update: fresh.metadata?.last_update ?? current?.metadata?.last_update ?? null,
        load_time_ms: fresh.metadata?.load_time_ms ?? 0,
      },
    };
    queryClient.setQueryData(['ranking', type, limit], merged);
    return merged;
  };

  return { ...query, refreshRanking };
}

export function getRankingErrorKey(error: unknown): 'ranking_error_auth' | 'ranking_error' {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) return 'ranking_error_auth';
    if (!error.response) return 'ranking_error';
  }
  return 'ranking_error';
}

export function prefetchRanking(queryClient: QueryClient) {
  return queryClient.prefetchQuery({
    queryKey: ['ranking', 'itempoints', 500],
    queryFn: () => fetchRanking('itempoints', 500),
    staleTime: STALE_MS,
  });
}

export function usePlayerRank(charId: number | null, type = 'itempoints') {
  return useQuery({
    queryKey: ['ranking', 'player', charId, type],
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; data: RankingPlayerRow }>(
        `/api/v1/ranking/player/${charId}`,
        { params: { type } },
      );
      return data.data;
    },
    enabled: charId != null && charId > 0,
    staleTime: STALE_MS,
  });
}
